import React, { useId, useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { SupportDutyTask, UpdateSupportDutyInput } from '../../features/monthly-support/types';
import './MonthlySupportPage.scss';

interface Props {
  task: SupportDutyTask;
  status: 'completed' | 'waived';
  onClose: () => void;
  onSave: (input: UpdateSupportDutyInput) => Promise<unknown>;
}

/** The same audited outcome form is used from support and the existing workspace. */
export default function SupportDutyDialog({ task, status, onClose, onSave }: Props) {
  const titleId = useId();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const complete = status === 'completed';
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) { setError(complete ? 'Record what was completed or link to the evidence.' : 'Record why this duty is being waived.'); return; }
    setBusy(true); setError('');
    try {
      await onSave({ taskId: task.id, status, ...(complete ? { evidence: note.trim() } : { waiverReason: note.trim() }) });
      onClose();
    } catch (e) { setError(e instanceof Error ? e.message : 'The duty could not be updated.'); }
    finally { setBusy(false); }
  };
  return <Modal show centered onHide={() => !busy && onClose()} aria-labelledby={titleId} backdrop={busy ? 'static' : true} dialogClassName="monthly-support-modal">
    <form onSubmit={submit}>
      <Modal.Header closeButton={!busy}><Modal.Title id={titleId}>{complete ? 'Complete duty' : 'Waive duty for this month'}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="mb-2">{task.title}</p><p className="ms-muted ms-small">{task.support.month} · The result is recorded with your identity and completion time.</p>
        {error && <div className="ms-notice ms-notice--error" role="alert">{error}</div>}
        <label className="ms-field">{complete ? 'Completion evidence' : 'Reason for waiver'}<textarea required autoFocus rows={4} value={note} maxLength={2000} disabled={busy} placeholder={complete ? 'Describe the result, or paste the report, task update or email reference.' : 'Why is this obligation not required this month?'} onChange={e => setNote(e.target.value)} /></label>
      </Modal.Body>
      <Modal.Footer><button type="button" className="ms-button" disabled={busy} onClick={onClose}>Cancel</button><button className="ms-button ms-button--primary" type="submit" disabled={busy}>{busy ? 'Saving…' : complete ? 'Record completion' : 'Record waiver'}</button></Modal.Footer>
    </form>
  </Modal>;
}
